import { MapPin, Phone, Clock, Wrench, CurrencyEur, MapPinLine, Check, X } from '@phosphor-icons/react';
import StatusBadge from './StatusBadge';
import CountdownTimer from './CountdownTimer';
import { Button } from '../ui/button';

const formatFullDateTime = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  const days = ['Dim.', 'Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.'];
  const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  const dayName = days[date.getDay()];
  const dayNum = date.getDate();
  const month = months[date.getMonth()];
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${dayName} ${dayNum} ${month} — ${hours}h${minutes}`;
};

const formatRelativeTime = (isoString) => {
  if (!isoString) return '';
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'à l\'instant';
  if (diffMins < 60) return `il y a ${diffMins} min`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `il y a ${diffHours}h`;
  return `il y a ${Math.floor(diffHours / 24)}j`;
};

export default function InterventionCard({ 
  intervention, 
  onValidate, 
  onCancel,
  onSelect,
  showValidateButton = false,
  showCancelButton = false,
  showCancelledDate = false
}) {
  const showLastPosition = ['en_route', 'en_cours'].includes(intervention.status) && intervention.last_position;
  const isCancelled = intervention.status === 'annulee';
  const isCompleted = ['terminee', 'encaissee'].includes(intervention.status);

  return (
    <div 
      className={`bg-white border rounded-xl p-5 hover:shadow-md hover:-translate-y-[2px] transition-all cursor-pointer ${
        isCancelled ? 'border-red-200 opacity-75' :
        isCompleted ? 'border-gray-200 opacity-70 hover:opacity-100 hover:border-reco-400' :
        'border-gray-200 hover:border-reco-400'
      }`}
      onClick={() => onSelect?.(intervention)}
      data-testid={`intervention-card-${intervention.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-gray-900 font-semibold text-lg">{intervention.client_name}</h3>
          <div className="flex items-center gap-1.5 text-gray-500 text-sm mt-1">
            <Phone size={14} />
            <a href={`tel:${intervention.client_phone}`} className="hover:text-reco-500"
              onClick={(e) => e.stopPropagation()}>
              {intervention.client_phone}
            </a>
          </div>
        </div>
        <StatusBadge status={intervention.status} />
      </div>

      <div className="space-y-2.5 text-sm">
        <div className="flex items-start gap-2 text-gray-600">
          <Wrench size={16} className="mt-0.5 text-gray-400 flex-shrink-0" />
          <span>{intervention.intervention_type}</span>
        </div>
        
        <div className="flex items-start gap-2 text-gray-600">
          <MapPin size={16} className="mt-0.5 text-gray-400 flex-shrink-0" />
          <span className="line-clamp-2">{intervention.address}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-600">
            <Clock size={16} className="text-gray-400" />
            <span>{formatFullDateTime(intervention.announced_time)}</span>
          </div>
          {!isCancelled && (
            <CountdownTimer 
              announcedTime={intervention.announced_time}
              validatedAt={intervention.validated_at}
              status={intervention.status}
            />
          )}
        </div>

        <div className="flex items-center gap-2 text-reco-600 font-semibold">
          <CurrencyEur size={16} />
          <span>{intervention.price?.toFixed(2)} €</span>
        </div>

        {intervention.assigned_technician_name && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Wrench size={14} className="text-gray-400" />
            <span>{intervention.assigned_technician_name}</span>
          </div>
        )}

        {showCancelledDate && (intervention.cancelled_at || intervention.created_at) && (
          <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-red-400">
            Annulée le {formatFullDateTime(intervention.cancelled_at || intervention.created_at)}
          </div>
        )}

        {showLastPosition && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-start gap-2 text-xs text-gray-500">
              <MapPinLine size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-medium text-gray-600">Dernière position : </span>
                <span>{intervention.last_position.address || 'Position GPS'}</span>
                <span className="text-gray-400"> — {formatRelativeTime(intervention.last_position.timestamp)}</span>
              </div>
            </div>
          </div>
        )}

        {intervention.notes && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-400 italic line-clamp-1">{intervention.notes}</p>
          </div>
        )}
      </div>

      {/* Action buttons for "En attente" */}
      {(showValidateButton || showCancelButton) && intervention.status === 'en_attente' && (
        <div className="flex gap-2 mt-4">
          {showValidateButton && (
            <Button
              onClick={(e) => { e.stopPropagation(); onValidate?.(intervention.id); }}
              className="flex-1 bg-reco-500 hover:bg-reco-600 text-white rounded-full gap-1.5"
              data-testid={`validate-btn-${intervention.id}`}
            >
              <Check size={16} weight="bold" />
              Valider
            </Button>
          )}
          {showCancelButton && (
            <Button
              variant="outline"
              onClick={(e) => { e.stopPropagation(); onCancel?.(intervention.id); }}
              className="border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600 rounded-full gap-1.5"
              data-testid={`cancel-btn-${intervention.id}`}
            >
              <X size={16} weight="bold" />
              Annuler
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
